import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import Lottie from "lottie-react";

import Button, { Color } from "../../Form/Button";
import confettiAnimation from "../../../assets/img/animations/confetti.json";
import ConfettiCustom from "../../ConfettiCustom";

type Props = {
  onComplete?: () => void;
  loading?: boolean;
};

export default function CompleteScreen(props: Props) {
  const { onComplete, loading } = props;
  const { t: translate } = useTranslation();
  const [showConfetti, setShowConfetti] = useState(true);
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const updateDimensions = () => {
      const container = document.querySelector('[data-cy="concludedScreen"]');
      if (container) {
        const rect = container.getBoundingClientRect();
        setContainerDimensions({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);

    return () => {
      window.removeEventListener('resize', updateDimensions);
      clearTimeout(timer);
    };
  }, []);

  return (
    <Box
      data-cy="concludedScreen"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        height: "100%",
        textAlign: "center",
        px: 4,
        pt: "20vh",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {showConfetti && containerDimensions.width > 0 && (
        <ConfettiCustom
          width={containerDimensions.width}
          height={containerDimensions.height}
          numberOfPieces={200}
          recycle={false}
          duration={7000}
          colors={['#0055A4', '#00A4FF', '#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1']}
          gravity={0.5}
          wind={0}
          friction={0.99}
        />
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
          maxWidth: 700,
          mx: "auto",
        }}
      >
        <Box sx={{ width: 96, height: 96 }}>
          <Lottie animationData={confettiAnimation} loop={true} />
        </Box>

        <Typography
          variant="h1"
          data-cy="concludedScreenTitle"
          sx={{
            fontFamily: "Manrope",
            fontWeight: 600,
            fontSize: "36px",
            lineHeight: "44px",
            letterSpacing: "-0.02em",
            color: "#0055A4",
            maxWidth: 650,
            px: 2,
          }}
        >
          {translate("onboarding.complete.title", {
            defaultValue: "Welcome to Shipeezi — where visibility, control, and simplicity come together."
          })}
        </Typography>

        <Button
          customColor={Color.SECONDARY}
          data-cy="get-start-button"
          onClick={onComplete}
          loading={loading}
          sx={{
            backgroundColor: "#0055A4",
            color: "#FFFFFF",
            width: 200,
            height: 48,
            fontSize: "16px",
            fontWeight: 600,
            borderRadius: "8px",
            mt: 2,
            "&:hover": {
              backgroundColor: "#003d82",
            },
          }}
        >
          {translate("onboarding.complete.button", { defaultValue: "Get started" })}
        </Button>
      </Box>
    </Box>
  );
}
